import type {AggregationSurveySubmissionListItem}
    from "crqlar-react-common/dist/models/aggregation/surveyAggregation.model";
import {Survey} from "crqlar-react-common/dist/models/survey/survey.model";
import {type SurveyAnswer, SurveyReviewState} from "crqlar-react-common/dist/models/survey/surveySubmission.model";
import {formatDateDMYHM} from "crqlar-react-common/dist/utils/date.util";
import {getLocalizedLangLabel} from "crqlar-react-common/dist/utils/translations.util";
import i18n from "i18next";
import {SURVEY_REVIEW_STATE_LABEL_KEYS} from "../../../common/uiElements/SurveyReviewStateElement";
import {surveyNpsClassLabelKey} from "../../../common/uiElements/SurveyNpsClassElement";
import {formatSurveyScore} from "../../../common/uiElements/SurveyScoreElement";
import {
    submissionQuestions,
    SURVEY_ANSWER_PART_SEPARATOR,
    SurveyAnswerContext,
    surveyAnswerParts,
    surveyQuestionTitle
} from "../../shared/surveyAnswer.util";
import {surveyMediaThumbnailUrl} from "../../shared/surveyAnswerMedia.util";
import {
    exportDaySlug,
    slugifyForFileName,
    type SurveyExportBranding
} from "../../shared/surveyExport.util";
import {surveyMarkdownToPlainText} from "../../shared/surveyMarkdownText.util";
import {surveySensitiveLabel} from "../../shared/surveyQuestionMarkers.util";
import {isWithheldSensitiveAnswer, SURVEY_SENSITIVE_MASK} from "../../shared/SurveySensitiveAnswerValue";
import {getSurveyQuestionTypeLabelKey} from "../../shared/surveyQuestionTypes.util";
import type {SurveySubmissionGuest} from "./useSurveySubmissionGuest";

export type SurveySubmissionDocumentFormat = 'pdf' | 'docx';

export type SurveySubmissionDocumentLine = {
    label: string
    value: string
    /** Secondary line under the value - a sector, a room type, a date range. */
    detail?: string
};

export type SurveySubmissionDocumentImage = {
    /** What the page draws: an upload's square thumbnail, or the signature as the guest drew it. */
    src: string
    /** The original, which the PDF and Word file link to. */
    href: string
};

export type SurveySubmissionDocumentAnswer = {
    question: string
    /** The answer's text; empty when the answer is only photos or a signature. */
    answer: string
    empty: boolean
    hidden: boolean
    required: boolean
    typeLabel: string
    sensitiveLabel?: string
    hiddenLabel?: string
    /** The uploaded photos, as the dialog's thumbnails, four to a row. */
    images: SurveySubmissionDocumentImage[]
    signature?: SurveySubmissionDocumentImage
};

export type SurveySubmissionDocumentGuest = {
    title: string
    name: string
    chips: string[]
    preferencesLabel?: string
    preferences?: string
    rows: SurveySubmissionDocumentLine[]
};

export type SurveySubmissionDocumentReview = {
    title: string
    stateLabel: string
    stateValue: string
    reviewedLine?: string
    notesLabel: string
    notes?: string
    emptyNotesLabel: string
};

export type SurveySubmissionDocumentData = {
    kicker: string
    surveyName: string
    organizationName: string
    logoDataUrl?: string
    /** DOM-only fallback: the print sheet can show a logo the writers could not embed. */
    logoUrl?: string
    branding?: SurveyExportBranding
    infoLines: SurveySubmissionDocumentLine[]
    answersTitle: string
    answers: SurveySubmissionDocumentAnswer[]
    emptyAnswersLabel: string
    /** Drawn in place of a photo or signature that could not be fetched. */
    imageMissingLabel: string
    signatureMissingLabel: string
    guest?: SurveySubmissionDocumentGuest
    review?: SurveySubmissionDocumentReview
    footer?: string
};

type SurveySubmissionDocumentInput = {
    submission: AggregationSurveySubmissionListItem
    survey?: Survey
    ctx: SurveyAnswerContext
    guest?: SurveySubmissionGuest
    organizationName?: string
    logoDataUrl?: string
    logoUrl?: string
    branding?: SurveyExportBranding
    /** Live editor state, so an unsaved review still prints what the user sees. */
    reviewState?: SurveyReviewState
    reviewNotes?: string
};

const MISSING = '—';

const documentImages = (answer: SurveyAnswer | undefined): SurveySubmissionDocumentImage[] => (
    (answer?.mediaValues ?? []).flatMap(media => {
        const src = surveyMediaThumbnailUrl(media);
        return src && media.mediaURI ? [{src, href: media.mediaURI}] : [];
    })
);

const documentSignature = (answer: SurveyAnswer | undefined): SurveySubmissionDocumentImage | undefined => {
    const href = answer?.signatureValue?.mediaURI;
    return href ? {src: href, href} : undefined;
};

const guestBlock = (
    guest: SurveySubmissionGuest | undefined,
    ctx: SurveyAnswerContext
): SurveySubmissionDocumentGuest | undefined => {
    if (!guest) return undefined;

    const preferences = guest.preferences
        ? [...guest.preferences.allergies, ...guest.preferences.diets, ...guest.preferences.medical]
            .filter(Boolean)
            .join(', ')
        : undefined;

    return {
        title: ctx.t('Guest'),
        name: guest.name,
        chips: guest.chips,
        preferencesLabel: preferences ? ctx.t('Allergies, Diets, Medical') : undefined,
        preferences: preferences || undefined,
        rows: guest.contextRows.map(row => ({
            label: row.label,
            value: row.title,
            detail: row.detail,
        })),
    };
};

const reviewBlock = (
    input: SurveySubmissionDocumentInput
): SurveySubmissionDocumentReview => {
    const {submission, ctx} = input;
    const reviewState = input.reviewState ?? submission.reviewState ?? SurveyReviewState.OPEN;
    const notes = surveyMarkdownToPlainText(input.reviewNotes ?? submission.reviewNotes)?.trim();

    const reviewedLine = submission.reviewedAt
        ? [
            `${ctx.t('Resolved at')}: ${formatDateDMYHM(submission.reviewedAt)}`,
            submission.reviewedByName ? `${ctx.t('Resolved by')}: ${submission.reviewedByName}` : undefined,
        ].filter(Boolean).join(' · ')
        : undefined;

    return {
        title: ctx.t('Status'),
        stateLabel: ctx.t('Review state'),
        stateValue: ctx.t(SURVEY_REVIEW_STATE_LABEL_KEYS[reviewState]),
        reviewedLine,
        notesLabel: ctx.t('Notes'),
        notes: notes || undefined,
        emptyNotesLabel: ctx.t('No notes yet.'),
    };
};

export const buildSurveySubmissionDocumentData = (
    input: SurveySubmissionDocumentInput
): SurveySubmissionDocumentData => {
    const {submission, survey, ctx} = input;
    const emptyLabel = ctx.t('Not answered');

    const infoLines: SurveySubmissionDocumentLine[] = [
        {
            label: ctx.t('Submitted'),
            value: submission.createdAt ? formatDateDMYHM(submission.createdAt) : MISSING,
        },
        {
            label: ctx.t('Language'),
            // In the UI language, like the dialog's language chip ("Englisch", not "English").
            value: submission.language
                ? getLocalizedLangLabel(submission.language, i18n.resolvedLanguage || i18n.language || '')
                : MISSING,
        },
        {
            label: ctx.t('Version'),
            value: submission.surveyVersion !== undefined ? String(submission.surveyVersion) : MISSING,
        },
    ];

    // The dialog's Score and NPS chips, left out like them when the response has none.
    if (submission.score != null && Number.isFinite(submission.score)) {
        infoLines.push({label: ctx.t('Score'), value: formatSurveyScore(submission.score)});
    }
    const npsLabelKey = surveyNpsClassLabelKey(submission.npsClass);
    if (npsLabelKey) {
        infoLines.push({label: ctx.t('NPS'), value: ctx.t(npsLabelKey)});
    }

    if (submission.sectorName) {
        infoLines.push({label: ctx.t('Sector'), value: submission.sectorName});
    }
    if (submission.reservationLabel) {
        infoLines.push({label: ctx.t('Reservation code'), value: submission.reservationLabel});
    }

    const answers: SurveySubmissionDocumentAnswer[] = submissionQuestions(survey?.questions, submission.answers)
        .map(question => {
            const entry = submission.answers?.find(candidate => candidate.questionId === question.id);
            const hidden = isWithheldSensitiveAnswer(entry, question, ctx);
            const answer = hidden
                ? SURVEY_SENSITIVE_MASK
                : surveyAnswerParts(entry, question, ctx).parts.map(part => part.text).join(SURVEY_ANSWER_PART_SEPARATOR);
            // A withheld answer arrives without its files, so these are empty for it.
            const images = documentImages(entry);
            const signature = documentSignature(entry);
            const empty = !answer && !images.length && !signature;

            return {
                question: surveyQuestionTitle(question, ctx),
                answer: empty ? emptyLabel : answer,
                empty,
                hidden,
                required: !!question.isRequired,
                typeLabel: ctx.t(getSurveyQuestionTypeLabelKey(question.type)),
                sensitiveLabel: surveySensitiveLabel(question, ctx),
                hiddenLabel: hidden ? ctx.t('Hidden') : undefined,
                images,
                signature,
            };
        });

    return {
        kicker: ctx.t('Submission'),
        surveyName: survey?.name || ctx.t('Survey'),
        organizationName: input.organizationName || '',
        logoDataUrl: input.logoDataUrl,
        logoUrl: input.logoUrl,
        branding: input.branding,
        infoLines,
        answersTitle: ctx.t('Answers'),
        answers,
        emptyAnswersLabel: ctx.t('This survey has no questions yet.'),
        imageMissingLabel: ctx.t('Image not available'),
        signatureMissingLabel: ctx.t('Signature not available'),
        guest: guestBlock(input.guest, ctx),
        review: reviewBlock(input),
        footer: `${ctx.t('Submission')}: ${submission.id}`,
    };
};

export const surveySubmissionFileName = (
    submission: AggregationSurveySubmissionListItem,
    survey: Survey | undefined,
    extension: SurveySubmissionDocumentFormat
) => {
    const slug = slugifyForFileName(survey?.name, 'survey');
    const day = exportDaySlug(submission.createdAt ?? new Date());
    const guest = slugifyForFileName(submission.profileName, '');

    return [
        'survey-submission',
        slug,
        guest || undefined,
        day,
    ].filter(Boolean).join('_') + `.${extension}`;
};
